import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { OptionsListMulti } from 'gung-common';
import { Observable, Subject, debounceTime, distinctUntilChanged, merge, skip } from 'rxjs';
import {
  SalesInsightsContact,
  SalesInsightsCustomerHealthResponse,
  SalesInsightsCustomerHealthRow,
  SalesInsightsFilterValues
} from '../../models/sales-insights.model';
import { SalesInsightsActivityService } from '../../services/sales-insights-activity.service';
import { SalesInsightsDelta, formatSalesInsightsAmount, salesInsightsDelta } from '../../utils/sales-insights-format';
import { salesInsightsConstantCase } from '../../utils/sales-insights-tiles';
import { SalesInsightsBaseComponent } from '../sales-insights-base/sales-insights-base.component';

export type SalesInsightsCustomerHealthColumn =
  | 'customer'
  | 'city'
  | 'salesperson'
  | 'lastPurchase'
  | 'lastContact'
  | 'nextContact'
  | 'invoicedYtd'
  | 'openOrders';

type SortDirection = 'asc' | 'desc';

export const SALES_INSIGHTS_CUSTOMER_FLAGS = [
  'visitRequired',
  'noVisitRequired',
  'noPurchase3m',
  'noPurchase1m',
  'noContact6m',
  'overdue'
];

const FLAG_TONES: { [flag: string]: string } = {
  overdue: 'danger',
  noPurchase3m: 'warning',
  noPurchase1m: 'warning',
  noContact6m: 'muted'
};

const TEXT_COLUMNS: SalesInsightsCustomerHealthColumn[] = ['customer', 'city', 'salesperson'];
const SEARCH_DEBOUNCE_MS = 300;
const COPIED_MS = 1500;
const ALL = '';

const PAGE_SIZE = 50;

@Component({
  selector: 'lib-sales-insights-customer-health',
  standalone: false,
  templateUrl: './sales-insights-customer-health.component.html',
  styleUrl: './sales-insights-customer-health.component.scss'
})
export class SalesInsightsCustomerHealthComponent
  extends SalesInsightsBaseComponent<SalesInsightsCustomerHealthResponse>
  implements OnInit
{
  protected translate = inject(TranslateService);
  protected activityService = inject(SalesInsightsActivityService);
  private destroy = inject(DestroyRef);

  readonly formatAmount = formatSalesInsightsAmount;
  readonly columns: SalesInsightsCustomerHealthColumn[] = [
    'customer',
    'city',
    'salesperson',
    'lastPurchase',
    'lastContact',
    'nextContact',
    'invoicedYtd',
    'openOrders'
  ];

  rows: SalesInsightsCustomerHealthRow[] = [];
  visibleCount = PAGE_SIZE;

  get visibleRows(): SalesInsightsCustomerHealthRow[] {
    return this.rows.slice(0, this.visibleCount);
  }

  showMore(): void {
    this.visibleCount += PAGE_SIZE;
  }
  search = '';
  flagOptions: OptionsListMulti[] = [];
  sortColumn: SalesInsightsCustomerHealthColumn = 'invoicedYtd';
  sortDirection: SortDirection = 'desc';
  exporting = false;
  copiedCustomerId: string | null = null;

  private searchChanges = new Subject<string>();

  get canExport(): boolean {
    return !!this.options.export && !this.loading && !this.error && this.rows.length > 0;
  }

  get canBook(): boolean {
    return !!this.options.bookActivity;
  }

  get flag(): string | null {
    return this.dashboardState.customerHealthFlag;
  }

  ngOnInit(): void {
    this.dashboardState.customerHealthFlag$
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(flag => (this.flagOptions = this.buildFlagOptions(flag)));
    super.ngOnInit();
  }

  protected fetch(filters: SalesInsightsFilterValues): Observable<SalesInsightsCustomerHealthResponse> {
    return this.salesInsightsService.getData<SalesInsightsCustomerHealthResponse>(
      {
        endpoint: this.config.source?.endpoint || 'customer-health',
        params: { ...(this.config.source?.params || {}), search: this.search.trim(), flag: this.flag || '' }
      },
      filters,
      this.config.accepts
    );
  }

  protected reloads(): Observable<unknown> {
    return merge(
      this.dashboardState.changes('activities', 'goals'),
      this.searchChanges.pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged()),
      this.dashboardState.customerHealthFlag$.pipe(skip(1), distinctUntilChanged())
    );
  }

  protected isEmpty(data: SalesInsightsCustomerHealthResponse | null): boolean {
    return !data?.rows?.length;
  }

  protected onData(data: SalesInsightsCustomerHealthResponse | null): void {
    this.rows = this.sortRows(data?.rows || []);
    this.visibleCount = PAGE_SIZE;
  }

  onSearch(value: string): void {
    this.search = value;
    this.searchChanges.next(value.trim());
  }

  selectFlag(selected: string[]): void {
    this.dashboardState.setCustomerHealthFlag(selected?.[0] || null);
  }

  sortBy(column: SalesInsightsCustomerHealthColumn): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = TEXT_COLUMNS.includes(column) || column === 'nextContact' ? 'asc' : 'desc';
    }
    this.rows = this.sortRows(this.rows);
  }

  columnLabelKey(column: SalesInsightsCustomerHealthColumn): string {
    return `SALES_INSIGHTS_HEALTH_COL_${salesInsightsConstantCase(column)}`;
  }

  isNumberColumn(column: SalesInsightsCustomerHealthColumn): boolean {
    return column === 'invoicedYtd' || column === 'openOrders';
  }

  shownFlags(row: { flags?: string[]; lastContact?: unknown }): string[] {
    const flags = row.flags || [];
    return flags.filter(
      flag =>
        !(flag === 'noPurchase1m' && flags.includes('noPurchase3m')) && !(flag === 'noContact6m' && !row.lastContact)
    );
  }

  flagLabelKey(flag: string): string {
    return `SALES_INSIGHTS_FLAG_${salesInsightsConstantCase(flag)}`;
  }

  flagTone(flag: string): string {
    return FLAG_TONES[flag] || 'muted';
  }

  salespersonName(row: SalesInsightsCustomerHealthRow): string {
    return row.salespersonName || row.salesperson || '';
  }

  contactTypeName(contact: SalesInsightsContact | null): string {
    return contact ? this.activityService.typeName(contact.type) : '';
  }

  daysAgo(days: number | null): string {
    if (days === null || days === undefined) {
      return '';
    }
    return days <= 0
      ? this.translate.instant('SALES_INSIGHTS_TODAY')
      : this.translate.instant('SALES_INSIGHTS_DAYS_AGO', { days: formatSalesInsightsAmount(days) });
  }

  invoicedDelta(row: SalesInsightsCustomerHealthRow): SalesInsightsDelta | null {
    return salesInsightsDelta(row.invoicedYtd, row.invoicedPrevYtd);
  }

  copyEmail(row: SalesInsightsCustomerHealthRow, event: Event): void {
    event.stopPropagation();
    if (!row.email || !navigator.clipboard) {
      return;
    }
    navigator.clipboard.writeText(row.email).then(
      () => {
        this.copiedCustomerId = row.customerId;
        setTimeout(() => {
          if (this.copiedCustomerId === row.customerId) {
            this.copiedCustomerId = null;
          }
        }, COPIED_MS);
      },
      () => {}
    );
  }

  book(row: SalesInsightsCustomerHealthRow): void {
    this.activityService.bookActivity(row.customerId).then(saved => {
      if (saved) {
        this.dashboardState.notifyChanged('activities');
      }
    });
  }

  async exportExcel(): Promise<void> {
    this.exporting = true;
    try {
      const XLSX = await import('xlsx');
      const t = (key: string) => this.translate.instant(key);
      const header = [
        t('SALES_INSIGHTS_HEALTH_COL_CUSTOMER_ID'),
        t('SALES_INSIGHTS_HEALTH_COL_CUSTOMER'),
        t('SALES_INSIGHTS_HEALTH_COL_CITY'),
        t('SALES_INSIGHTS_HEALTH_COL_EMAIL'),
        t('SALES_INSIGHTS_HEALTH_COL_SALESPERSON'),
        t('SALES_INSIGHTS_HEALTH_COL_STATUS'),
        t('SALES_INSIGHTS_HEALTH_COL_LAST_PURCHASE'),
        t('SALES_INSIGHTS_HEALTH_COL_LAST_CONTACT'),
        t('SALES_INSIGHTS_HEALTH_COL_LAST_CONTACT_TYPE'),
        t('SALES_INSIGHTS_HEALTH_COL_NEXT_CONTACT'),
        t('SALES_INSIGHTS_HEALTH_COL_NEXT_CONTACT_TYPE'),
        t('SALES_INSIGHTS_HEALTH_COL_INVOICED_YTD'),
        t('SALES_INSIGHTS_HEALTH_COL_INVOICED_PREV_YTD'),
        t('SALES_INSIGHTS_HEALTH_COL_OPEN_ORDERS')
      ];
      const body = this.rows.map(row => [
        row.customerId,
        row.customerName,
        row.city || '',
        row.email || '',
        this.salespersonName(row),
        (row.flags || []).map(flag => t(this.flagLabelKey(flag))).join(', '),
        row.lastPurchaseDate || '',
        row.lastContact?.date || '',
        this.contactTypeName(row.lastContact),
        row.nextContact?.date || '',
        this.contactTypeName(row.nextContact),
        row.invoicedYtd ?? null,
        row.invoicedPrevYtd ?? null,
        row.openOrderValue ?? null
      ]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([header, ...body]), 'Data');
      XLSX.writeFile(workbook, `${this.fileName()}.xlsx`);
    } finally {
      this.exporting = false;
    }
  }

  private fileName(): string {
    const title = this.translate.instant(this.config?.title || 'SALES_INSIGHTS_CUSTOMER_HEALTH');
    return title.replace(/[\\/:*?"<>|]+/g, '').trim() || 'customer-health';
  }

  private buildFlagOptions(selected: string | null): OptionsListMulti[] {
    const flags = (this.options.flags as string[] | undefined)?.length ? (this.options.flags as string[]) : SALES_INSIGHTS_CUSTOMER_FLAGS;
    return [
      { id: ALL, name: this.translate.instant('SALES_INSIGHTS_FLAG_ALL'), selected: !selected },
      ...flags.map(flag => ({ id: flag, name: this.translate.instant(this.flagLabelKey(flag)), selected: flag === selected }))
    ];
  }

  // Missing values sort last in both directions, so customers without purchases never crowd the top.
  private sortRows(rows: SalesInsightsCustomerHealthRow[]): SalesInsightsCustomerHealthRow[] {
    const factor = this.sortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const left = this.sortValue(a);
      const right = this.sortValue(b);
      if (left === null || right === null) {
        return left === right ? 0 : left === null ? 1 : -1;
      }
      return (typeof left === 'string' ? left.localeCompare(right as string, 'sv') : left - (right as number)) * factor;
    });
  }

  private sortValue(row: SalesInsightsCustomerHealthRow): string | number | null {
    switch (this.sortColumn) {
      case 'customer':
        return row.customerName || row.customerId || null;
      case 'city':
        return row.city || null;
      case 'salesperson':
        return this.salespersonName(row) || null;
      case 'lastPurchase':
        return row.lastPurchaseDate || null;
      case 'lastContact':
        return row.lastContact?.date || null;
      case 'nextContact':
        return row.nextContact?.date || null;
      case 'invoicedYtd':
        return row.invoicedYtd ?? null;
      case 'openOrders':
        return row.openOrderValue ?? null;
    }
  }
}
